import { useCallback, useState } from 'react';
import { notify } from '@/lib/notify';
import { isSupportedFile, MAX_FILE_SIZE_MB, type StagedSource } from './types';

const MAX_FILE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Picked or dropped files -> `onAdd`, one at a time, after the checks every
 * entry point must apply the same way: supported type, the size cap, and the
 * caller's remaining slots. Shared by the desktop drop zone
 * (`UploadFileTab`) and the phone sheet's "Upload a file" row
 * (`AttachSourceSheet`), so neither can drift from the other.
 */
export function useStageFiles(
  onAdd: (source: StagedSource) => void | Promise<void>,
  remainingFileSlots?: number
) {
  const [isBusy, setIsBusy] = useState(false);

  const stageFiles = useCallback(
    async (selected: File[]) => {
      const accepted = selected.filter(file => {
        if (!isSupportedFile(file.name)) {
          notify.error(`"${file.name}" - file type not supported.`);
          return false;
        }
        if (file.size > MAX_FILE_BYTES) {
          notify.error(
            `"${file.name}" exceeds the ${MAX_FILE_SIZE_MB} MB limit.`
          );
          return false;
        }
        return true;
      });
      if (accepted.length === 0) return;

      let toStage = accepted;
      if (
        remainingFileSlots !== undefined &&
        accepted.length > remainingFileSlots
      ) {
        notify.error(
          `Only ${remainingFileSlots} more file${remainingFileSlots === 1 ? '' : 's'} can be added.`
        );
        toStage = accepted.slice(0, remainingFileSlots);
        if (toStage.length === 0) return;
      }

      setIsBusy(true);
      try {
        for (const file of toStage) {
          await onAdd({ kind: 'file', file });
        }
      } catch (err: unknown) {
        notify.error(err, "Couldn't add file");
      } finally {
        setIsBusy(false);
      }
    },
    [onAdd, remainingFileSlots]
  );

  return { stageFiles, isBusy };
}
